import * as R from 'remeda';
import { Action, Location } from 'history';
import { CALL_HISTORY_METHOD, LOCATION_CHANGE } from 'redux-first-history';
import { ActionsObservable, StateObservable } from 'redux-observable';
import { Observable } from 'rxjs/internal/Observable';
import {
  ActionCreator,
  createAction,
  isActionOf as originalIsActionOf,
} from 'typesafe-actions';
import { EpicDependencies } from '../../lib/apiTypes';
import { AnyDatapointDataST } from '../../types/datapoints';
import { State } from '../../types/state';
import { RootActionType } from '../rootActions';

export const ensureArray = <T>(maybeArray: Array<T> | T): Array<number> =>
  ([] as T[]).concat(maybeArray).map(Number);

export const updateInArray = <T>(
  array: Array<T>,
  index: number,
  updateFn: (_item: T) => T
) => {
  const item = array[index];
  if (item === undefined) return array;
  return [...array.slice(0, index), updateFn(item), ...array.slice(index + 1)];
};

export const insertInArray = <T>(array: Array<T>, index: number, data: T) => [
  ...array.slice(0, index),
  data,
  ...array.slice(index),
];

export const insertArrayInArray =
  <T>(index: number, data: Array<T>) =>
  (array: T[]) => [...array.slice(0, index), ...data, ...array.slice(index)];

export const swapItemsInArray = <T>(
  array: Array<T>,
  fromIndex: number,
  toIndex: number
) => {
  const newArray = Array.from(array);
  const removed = newArray.splice(fromIndex, 1);
  const item = removed[0];
  if (item !== undefined) {
    newArray.splice(toIndex, 0, item);
  }
  return newArray;
};

export const removeIndexInArray = <T>(array: Array<T>, index: number) =>
  array.filter((_, i) => i !== index);

export const findLastIndex = (
  datapoints: Array<AnyDatapointDataST>,
  parentIndex: number
): number => {
  const datapoint = datapoints[parentIndex];
  const currentChildren = datapoint
    ? 'children' in datapoint
      ? datapoint.children
      : []
    : [];

  const lastChild = currentChildren[currentChildren.length - 1];

  return R.isEmpty(currentChildren) || !lastChild
    ? parentIndex
    : findLastIndex(datapoints, lastChild.index);
};

/**
 * A convenience function to define a new epic.
 * This way you don't need to specify types of the parameters (and you don't need to import them).
 */
export const makeEpic = <T>(
  epic: (
    _action$: ActionsObservable<RootActionType>,
    _state$: StateObservable<State>,
    _dependencies: EpicDependencies
  ) => Observable<T>
) => epic;

/**
 * Return type of original isActionOf from typesafe-actions library
 * is determined as union of all possible actions.
 * For example, when the return type is { type: 'A', meta: M } | { type: 'B' },
 * you can't access 'action.meta' without type narrowing. However, in the codebase there are a lot of places
 * which try to access 'meta' property in this situation.
 *
 * This isActionOf determines the return type a bit differently - in the same situation
 * it will be { type: 'A', meta: M } | { type: 'B', meta?: undefined }
 * so the rest of the code can safely access the 'meta' property.
 *
 * The same applies to 'payload' property.
 */
export function isActionOf<T extends string, AC extends ActionCreator<T>>(
  actionCreators: AC[] | AC
): (action: { type: string }) => action is ReturnType<AC> {
  const result = (_action: { type: string }) =>
    (actionCreators instanceof Array ? actionCreators : [actionCreators]).some(
      ac => {
        return originalIsActionOf(ac, _action);
      }
    );

  return result as (action: { type: string }) => action is ReturnType<AC>;
}

/**
 * LOCATION_CHANGE and CALL_HISTORY_METHOD actions come from redux-first-history.
 * For consistency, we want to use then in filter(isActionOf(...))
 * in the same way as our own action creators,
 * that's why we create an action creators for them.
 */

export const locationChange = createAction(
  LOCATION_CHANGE,
  (
    _
  ): {
    location: Location;
    action: Action;
  } => {
    throw new Error(
      "locationChange action creator shouldn't be called directly"
    );
  }
)();

export const callHistoryMethod = createAction(
  CALL_HISTORY_METHOD,
  (
    _
  ): {
    method: string;
    args?: unknown[];
  } => {
    throw new Error(
      "callHistoryMethod action creator shouldn't be called directly"
    );
  }
)();
